import type { DirectoryElementsWithPermissions } from '@repo/types/directory';
import type { UseMutationResult } from '@tanstack/react-query';

import { FileSystemService } from '@repo/api/file-system/fileSystemService';
import { cn } from '@repo/lib/cn';
import { isAxiosError } from 'axios';
import { ChevronDownIcon, FileArchiveIcon, RotateCcwIcon, XIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Button } from '@/components/ui/button';
import {
  FileUploadList,
  FileUploadListAction,
  FileUploadListActions,
  FileUploadListDescription,
  FileUploadListDescriptionText,
  FileUploadListHeader,
  FileUploadListIcon,
  FileUploadListInfo,
  FileUploadListItem,
  FileUploadListName,
} from '@/components/ui/file-upload';
import { getFileUrl } from '@/utils/file';

interface DownloadCompressingElement {
  UUID: string;
  name: string;
  amount: number;
  status: UseMutationResult['status'];
  label: string;
  progress: number | null;
  abort: () => void;
  retry: () => void;
}

/**
 * Props for the DownloadFSEntry component.
 * @property {DirectoryElementsWithPermissions} elements - Elements to download.
 * @property {boolean} open - Whether the drawer is open.
 * @property {(open: boolean) => void} onOpenChange - Callback for drawer open state.
 * @property {() => void} [callback] - Optional callback after download.
 */
interface DownloadFSEntryProps {
  elements: DirectoryElementsWithPermissions;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  callback?: () => void;
}

export default function DownloadFSEntry(downloadFSEntryProps: DownloadFSEntryProps) {
  const { elements, open, onOpenChange, callback } = downloadFSEntryProps;
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const [downloadingElements, setDownloadingElements] = useState<DownloadCompressingElement[] | null>(null);

  const updateDownloadingElement = useCallback((UUID: string, patch: Partial<Omit<DownloadCompressingElement, 'UUID'>>) => {
    setDownloadingElements((prev) => {
      if (!prev) return prev;

      return prev.map((element) => {
        if (element.UUID !== UUID) return element;
        return { ...element, ...patch };
      });
    });
  }, []);

  const downloadSelection = useCallback(
    async (files: DirectoryElementsWithPermissions['files'], directories: DirectoryElementsWithPermissions['directories']) => {
      const abortController = new AbortController();
      const UUID: string = uuidv4();
      const zipFileName: string = `${UUID}-${new Date().toDateString()}.zip`;

      const appendElement = () => {
        const newElement: DownloadCompressingElement = {
          UUID,
          name: zipFileName,
          amount: files.length + directories.length,
          status: 'pending',
          label: 'Przygotowywanie ZIP',
          progress: null,
          abort: () => abortController.abort(),
          retry: () => {
            void downloadSelection(files, directories);
          },
        };

        setDownloadingElements((prev) => (prev ? [...prev, newElement] : [newElement]));
      };

      setOpenDrawer(true);
      appendElement();

      try {
        const archive = await FileSystemService.downloadFsItems(
          {
            fileIds: files.map((element) => element.id),
            dirIds: directories.map((element) => element.id),
          },
          {
            signal: abortController.signal,
            onDownloadProgress: (progressEvent) => {
              const progress = progressEvent.total && progressEvent.total > 0 ? Math.min(100, Math.round((progressEvent.loaded / progressEvent.total) * 100)) : null;

              updateDownloadingElement(UUID, {
                status: 'pending',
                label: 'Pobieranie ZIP',
                progress,
              });
            },
          }
        );

        const archiveUrl = URL.createObjectURL(archive.data);
        const link = document.createElement('a');
        link.href = archiveUrl;
        link.setAttribute('download', zipFileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(archiveUrl);

        updateDownloadingElement(UUID, {
          status: 'success',
          label: 'Pobieranie zakończone',
          progress: 100,
        });
      } catch (error) {
        const isCanceled: boolean = isAxiosError(error) && error.code === 'ERR_CANCELED';

        updateDownloadingElement(UUID, {
          status: 'error',
          label: isCanceled ? 'Pobieranie anulowane' : 'Pobieranie nie powiodło się',
          progress: null,
        });
      }
    },
    [updateDownloadingElement]
  );

  const onDownload = useCallback(async () => {
    const { files, directories } = { ...elements };
    onOpenChange(false);
    callback?.();

    const firstFile = files[0];
    if (files.length === 1 && directories.length === 0 && firstFile) {
      // Single file download
      const url = getFileUrl(firstFile.id);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', firstFile.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else {
      await downloadSelection(files, directories);
    }
  }, [callback, downloadSelection, elements, onOpenChange]);

  useEffect(() => {
    // downloadInitiated.current = false;
    if (open && (elements.files.length > 0 || elements.directories.length > 0)) {
      onDownload();
      //   downloadInitiated.current = true;
    }
  }, [open, elements, onDownload]);

  useEffect(() => {
    if (!openDrawer) {
      setDownloadingElements(null);
    }
  }, [openDrawer]);

  return (
    <DownloadFsEntryDrawer open={openDrawer} onOpenChange={setOpenDrawer}>
      <FileUploadList>
        {downloadingElements?.map((el) => (
          <DownloadFsEntryListElement key={el.UUID} {...el} />
        ))}
      </FileUploadList>
    </DownloadFsEntryDrawer>
  );
}

function DownloadFsEntryListElement(downloadCompressingElement: DownloadCompressingElement) {
  const { name, amount, abort, retry, status, label, progress } = downloadCompressingElement;
  return (
    <FileUploadListItem>
      <FileUploadListHeader>
        <FileUploadListIcon>
          <FileArchiveIcon />
        </FileUploadListIcon>
        <FileUploadListInfo>
          <FileUploadListName className={cn(status === 'error' && 'text-text-destructive')}>{name}</FileUploadListName>
          <FileUploadListDescription>
            <FileUploadListDescriptionText>{amount} element/ów</FileUploadListDescriptionText>
            <FileUploadListDescriptionText>{progress !== null ? `${label} (${progress}%)` : label}</FileUploadListDescriptionText>
          </FileUploadListDescription>
        </FileUploadListInfo>
        <FileUploadListActions>
          {/* Retry button */}
          <FileUploadListAction className={cn(status === 'error' ? 'visible' : 'hidden')} onClick={() => retry()}>
            <RotateCcwIcon />
            <span className="sr-only">Retry</span>
          </FileUploadListAction>

          {/* Abort button */}
          <FileUploadListAction className={cn(status === 'pending' ? 'visible' : 'hidden')} onClick={() => abort()}>
            <XIcon />
            <span className="sr-only">Abort</span>
          </FileUploadListAction>
        </FileUploadListActions>
      </FileUploadListHeader>
    </FileUploadListItem>
  );
}
type DownloadFsEntryDrawerProps = {
  callback?: () => void;
  children?: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

function DownloadFsEntryDrawer({ open, onOpenChange, callback, children }: DownloadFsEntryDrawerProps) {
  const [openCollapsible, setOpenCollapsible] = useState<boolean>(true);

  const collapsibleToggle = () => {
    setOpenCollapsible((prev) => !prev);
  };

  const onClose = () => {
    callback?.();
    onOpenChange(false);
    setOpenCollapsible(true);
  };

  return (
    <div
      aria-describedby={'file-upload-list'}
      className={cn(
        'border-border-primary bg-surface-primary fixed inset-x-0 right-0 bottom-0 left-0 z-20 mt-12 flex h-auto max-h-96 w-full flex-col rounded-t-lg border transition-transform duration-300 md:right-6 md:left-auto md:w-96',
        open ? 'translate-y-0' : 'translate-y-full'
      )}
      style={{ pointerEvents: open ? 'auto' : 'none' }}
    >
      <header className={'flex w-full justify-end p-2'}>
        <h5 className="sr-only">File download list</h5>
        <Button size={'sm'} variant={'ghost'} onClick={collapsibleToggle}>
          <ChevronDownIcon className={cn(!openCollapsible && 'rotate-180')} />
        </Button>
        <Button onClick={onClose} size={'sm'} variant={'ghost'}>
          <XIcon />
        </Button>
      </header>
      <div className={cn('h-full overflow-auto transition-all duration-200', openCollapsible ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0')}>
        <div className={'flex h-full flex-col gap-2 p-4'}>{children}</div>
      </div>
    </div>
  );
}
